import * as React from 'react';
import { useRecoilValue } from 'recoil';
import {
  IRide,
  IRideMapSpeedLimitLines,
  IRidePlayerSpeedLimitsSettings,
  IRidePosition,
  IRideSpeedLimitLinePaths,
  TRideActiveLayer,
} from '../types';
import { IS_RIDE_LAYER } from '../constants';
import {
  activeRideNumberAtom,
  enableSpeedlimitsAtom,
  isRideActiveAtom,
  selectedRidesAtom,
  showAllRidesAtom,
  speedlimitSettingsAtom,
} from '../RidePlayerState';
import {
  createSpeedLimitLines,
  createSpeedLimitLinesForRide,
  updateSpeedLimitLines,
} from '../RidePlayerService';

interface IUseSpeedLimitLine {
  speedLimitLines: IRideMapSpeedLimitLines;
  speedLimitPaths: IRideSpeedLimitLinePaths;
  setSpeedLimitLinesSize: (strokeWeight: number) => void;
  showAllSpeedLimitLines: () => void;
  hideAllSpeedLimitLines: () => void;
  setSpeedLimitLinesOpacity: (rideNumber: number, opacity: number) => void;
  setAllSpeedLimitLinesColor: (color: string, exceptRideNumber?: number) => void;
  createSpeedLimitLinesForRide: (rideNumber: number) => void;
  createSpeedLimitLines: (isShowAllRidesEnabled: boolean) => void;
  removeAllSpeedLimitLines: () => void;
  setSpeedLimitLineZIndex: (rideNumber: number, zIndex: number) => void;
  setAllSpeedLimitLinesZIndex: (zIndex: number, exceptRideNumber?: number) => void;
}

/**
 * useRidePlayerSpeedLimitLine custom hook
 * @param {google.maps.Map} map
 * @returns {IUseSpeedLimitLine}
 */
const useSpeedLimitLine = (
  map: google.maps.Map,
  rides: IRide[],
  activeLayer: TRideActiveLayer,
  actualRidePosition?: IRidePosition | null
): IUseSpeedLimitLine => {
  const speedLimitLines = React.useRef<IRideMapSpeedLimitLines>({});
  const speedLimitPaths = React.useRef<IRideSpeedLimitLinePaths>({});
  const speedLimitsSettings =
    useRecoilValue<IRidePlayerSpeedLimitsSettings>(speedlimitSettingsAtom);
  const enableSpeedlimits = useRecoilValue<boolean>(enableSpeedlimitsAtom);
  const selectedRides = useRecoilValue<number[]>(selectedRidesAtom);
  const activeRide = useRecoilValue<number>(activeRideNumberAtom);
  const isRideActive = useRecoilValue<number | false>(isRideActiveAtom);
  const showAllRides = useRecoilValue<boolean>(showAllRidesAtom);

  /**
   * Set online lines stroke weight (aka size)
   * @param {number} strokeWeight
   * @returns {void}
   */
  const setSpeedLimitLinesSize = (strokeWeight = 2): void => {
    Object.keys(speedLimitLines.current).forEach((rideNumber: string) => {
      const polylines = speedLimitLines[rideNumber];
      polylines.forEach((polyline: any) => {
        if (polyline.strokeWeight !== strokeWeight) {
          polyline.setOptions({ strokeWeight });
        }
      });
    });
  };

  /**
   * Set all speedlimit lines color
   * @param {string} color
   * @param {number} exceptRideNumber
   * @returns {void}
   */
  const setAllSpeedLimitLinesColor = (color: string, exceptRideNumber?: number): void => {
    if (speedLimitLines.current) {
      Object.keys(speedLimitLines.current).forEach((rideNumber: string) => {
        if (exceptRideNumber && exceptRideNumber === Number(rideNumber)) {
          return;
        }
        const polylines: google.maps.Polyline[] = speedLimitLines.current[rideNumber];
        polylines.forEach((polyline: google.maps.Polyline) => {
          polyline.setOptions({ strokeColor: color });
        });
      });
    }
  };

  /**
   * Create speedlimit lines for active ride
   * @param {number} rideNumber
   * @returns {void}
   */
  const createRidePlayerSpeedLimitLinesForRide = (rideNumber: number): void => {
    let showOnMap = false;
    if (activeLayer === IS_RIDE_LAYER && enableSpeedlimits) {
      showOnMap = true;
    }
    createSpeedLimitLinesForRide(
      showOnMap ? map : undefined,
      speedLimitPaths.current,
      speedLimitLines.current,
      rideNumber
    );
  };

  /**
   * Create speedlimit lines based on settings
   * @returns {void}
   */
  const createRidePlayerSpeedLimitLines = (isShowAllRidesEnabled: boolean): void => {
    let showOnMap = false;
    if (activeLayer === IS_RIDE_LAYER && enableSpeedlimits) {
      showOnMap = true;
    }
    speedLimitLines.current = {};
    speedLimitLines.current = createSpeedLimitLines(
      showOnMap ? map : null,
      rides,
      selectedRides,
      speedLimitPaths.current,
      speedLimitsSettings,
      isRideActive,
      activeLayer,
      isShowAllRidesEnabled
    );
  };

  /**
   * Show all speedlimit lines
   * @returns {void}
   */
  const showAllSpeedLimitLines = (): void => {
    Object.keys(speedLimitLines.current).forEach((rideNumber: string) => {
      speedLimitLines.current[Number(rideNumber)].forEach((polyline: google.maps.Polyline) => {
        if (!polyline.getMap() && selectedRides.includes(Number(rideNumber))) {
          polyline.setMap(map);
        }
      });
    });
  };

  /**
   * Hidel all speedlimit lines
   * @returns {void}
   */
  const hideAllSpeedLimitLines = (remove = false): void => {
    Object.keys(speedLimitLines.current).forEach((rideNumber: string) => {
      speedLimitLines.current[Number(rideNumber)].forEach((polyline: google.maps.Polyline) => {
        if (polyline.getMap()) {
          polyline.setMap(null);
        }
      });
      if (remove) {
        delete speedLimitLines.current[Number(rideNumber)];
      }
    });
  };

  /**
   * Remove all speedlimit lines from map
   */
  const removeAllSpeedLimitLines = (): void => {
    hideAllSpeedLimitLines();
    speedLimitLines.current = {};
  };

  /**
   * Set alarm line opacity for specified ride
   * @param {number} rideNumber
   * @param {number} opacity
   */
  const setSpeedLimitLinesOpacity = (rideNumber: number, opacity: number): void => {
    if (speedLimitLines.current && speedLimitLines.current[rideNumber]) {
      const polylines = speedLimitLines.current[String(rideNumber)];
      polylines.forEach((polyline: any) => {
        if (polyline && polyline.strokeOpacity) {
          polyline.setOptions({ strokeOpacity: opacity });
        }
      });
    }
  };

  /**
   * Set speedlimit line zIndex for specified ride
   * @param {number} rideNumber
   * @param {number} zIndex
   * @returns {void}
   */
  const setSpeedLimitLineZIndex = (rideNumber: number, zIndex: number): void => {
    if (speedLimitLines.current && speedLimitLines.current[rideNumber]) {
      const polylines = speedLimitLines.current[String(rideNumber)];
      polylines.forEach((polyline: any) => {
        if (polyline && polyline.zIndex) {
          polyline.setOptions({ zIndex });
        }
      });
    }
  };

  /**
   * Set all speedlimit lines zIndex
   * @param {number} zIndex
   * @param {number} exceptRideNumber
   * @returns {void}
   */
  const setAllSpeedLimitLinesZIndex = (zIndex: number, exceptRideNumber?: number): void => {
    if (speedLimitLines.current) {
      Object.keys(speedLimitLines.current).forEach((rideNumber: string) => {
        if (exceptRideNumber && exceptRideNumber === Number(rideNumber)) {
          return;
        }
        const polylines: google.maps.Polyline[] = speedLimitLines.current[rideNumber];
        polylines.forEach((polyline: google.maps.Polyline) => {
          polyline.setOptions({ zIndex });
        });
      });
    }
  };

  // Render speedlimit lines, when activeRide and speedlimits changes
  React.useEffect(() => {
    createRidePlayerSpeedLimitLines(showAllRides);
    return () => {
      hideAllSpeedLimitLines();
      speedLimitPaths.current = {};
      speedLimitLines.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableSpeedlimits, speedLimitsSettings]);

  // If actual position is present, update speedlimit lines
  React.useEffect(() => {
    if (actualRidePosition && activeRide) {
      let showOnMap = false;
      if (activeLayer === IS_RIDE_LAYER && enableSpeedlimits) {
        showOnMap = true;
      }
      updateSpeedLimitLines(
        showOnMap ? map : null,
        speedLimitLines.current,
        speedLimitPaths.current,
        speedLimitsSettings,
        activeRide,
        actualRidePosition
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualRidePosition, activeRide]);

  // Handle show/hide speedlimit lines by selected rides
  React.useEffect(() => {
    Object.keys(speedLimitLines.current).forEach((rideNumber: string) => {
      speedLimitLines.current[Number(rideNumber)].forEach((polyline: google.maps.Polyline) => {
        if (!selectedRides.includes(Number(rideNumber)) && polyline.getMap()) {
          polyline.setMap(null);
        }
        if (
          selectedRides.includes(Number(rideNumber)) &&
          !polyline.getMap() &&
          activeLayer === IS_RIDE_LAYER &&
          enableSpeedlimits
        ) {
          polyline.setMap(map);
        }
      });
    });
  }, [activeLayer, enableSpeedlimits, map, selectedRides]);

  const data: IUseSpeedLimitLine = {
    speedLimitLines: speedLimitLines.current,
    speedLimitPaths: speedLimitPaths.current,
    setSpeedLimitLinesSize,
    showAllSpeedLimitLines,
    hideAllSpeedLimitLines,
    setSpeedLimitLinesOpacity,
    setAllSpeedLimitLinesColor,
    createSpeedLimitLinesForRide: createRidePlayerSpeedLimitLinesForRide,
    createSpeedLimitLines: createRidePlayerSpeedLimitLines,
    removeAllSpeedLimitLines,
    setSpeedLimitLineZIndex,
    setAllSpeedLimitLinesZIndex,
  };

  return data;
};

export default useSpeedLimitLine;
